import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { reportAxiosError } from '~/Utils';

// Generic interfaces for the hook
export interface IdsResponse<IdType, ExtraDataType> {
  ids: IdType[];
  results_truncated: boolean;
  extra_data: ExtraDataType | null;
}

export interface PageDataResponse<PageResultType, PageMetaDataType> {
  results: PageResultType[];
  meta: PageMetaDataType | null;
}

export interface UseIdBasedPaginationProps {
  fetchIdsUrl: string;
  fetchPageDataUrl: string;
  fetchIdsParams: Record<string, unknown>;
  fetchPageDataParams?: Record<string, unknown>;
  pageDataIdKey?: string; // Key name for IDs in the fetchPageData request (defaults to 'ids')
  initialRowsPerPage?: number;
  maxRowsPerPage?: number;
}

export interface PaginationState {
  page: number;
  rowsPerPage: number;
  totalCount: number;
  resultsTruncated: boolean;
  setPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
}

export interface UseIdBasedPaginationReturn<PageResultType, ExtraDataType, PageMetaDataType> {
  // Pagination state
  pagination: PaginationState;

  // Data
  pageResults: PageResultType[];
  extraData: ExtraDataType | null;
  pageMeta: PageMetaDataType | null;

  // Loading states
  isLoading: boolean;
  isError: boolean;

  // Actions
  reload: () => void;
  reloadKeepingPage: () => Promise<void>;
  refreshPageData: () => Promise<PageResultType[]>;
  updatePageResult: (item: PageResultType) => void;
}

const useIdBasedPagination = <IdType, ExtraDataType, PageResultType, PageMetaDataType>(
  props: UseIdBasedPaginationProps
): UseIdBasedPaginationReturn<PageResultType, ExtraDataType, PageMetaDataType> => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(props.initialRowsPerPage || 10);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Data states
  const [allIds, setAllIds] = useState<IdType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [resultsTruncated, setResultsTruncated] = useState(false);
  const [extraData, setExtraData] = useState<ExtraDataType | null>(null);
  const [pageResults, setPageResults] = useState<PageResultType[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMetaDataType | null>(null);
  const [hasLoadedIds, setHasLoadedIds] = useState(false);

  // Memoize params using JSON.stringify for deep comparison
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFetchIdsParams = useMemo(() => props.fetchIdsParams, [JSON.stringify(props.fetchIdsParams)]);
  const memoizedFetchPageDataParams = useMemo(
    () => props.fetchPageDataParams,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(props.fetchPageDataParams)]
  );

  // Fetch IDs function
  const fetchIds = useCallback(async (): Promise<IdType[] | null> => {
    try {
      setIsLoading(true);
      setIsError(false);

      const response = await axios.post<IdsResponse<IdType, ExtraDataType>>(props.fetchIdsUrl, memoizedFetchIdsParams);

      const { ids, results_truncated, extra_data } = response.data;
      setAllIds(ids);
      setTotalCount(ids.length);
      setResultsTruncated(results_truncated);
      setExtraData(extra_data || null);
      setHasLoadedIds(true);

      if (ids.length === 0) {
        setIsLoading(false);
      }

      return ids;
    } catch (error) {
      reportAxiosError(error);
      setIsError(true);
      setIsLoading(false);
      return null;
    }
  }, [props.fetchIdsUrl, memoizedFetchIdsParams]);

  // Fetch page data function
  const fetchPageData = useCallback(async (): Promise<PageResultType[]> => {
    if (allIds.length === 0) {
      // Only clear results if we have loaded IDs at least once
      // This fixes the bug where empty filter results show stale data
      // but preserves loading state during initial load
      if (hasLoadedIds) {
        setPageResults([]);
        setPageMeta(null);
        setIsLoading(false);
      }
      return [];
    }

    try {
      setIsError(false);

      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      const pageIds = allIds.slice(startIndex, endIndex);

      if (pageIds.length === 0) {
        setPageResults([]);
        setPageMeta(null);
        setIsLoading(false);
        return [];
      }

      const idKey = props.pageDataIdKey || 'ids';
      const requestPayload = {
        [idKey]: pageIds,
        ...(memoizedFetchPageDataParams || {}),
      };

      const response = await axios.post<PageDataResponse<PageResultType, PageMetaDataType>>(
        props.fetchPageDataUrl,
        requestPayload
      );

      const results = response.data.results;
      setPageResults(results);
      setPageMeta(response.data.meta || null);
      return results;
    } catch (error) {
      reportAxiosError(error);
      setIsError(true);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [
    allIds,
    page,
    rowsPerPage,
    props.fetchPageDataUrl,
    memoizedFetchPageDataParams,
    props.pageDataIdKey,
    hasLoadedIds,
  ]);

  // Effects
  // Fetch IDs when params change
  useEffect(() => {
    fetchIds();
  }, [fetchIds]);

  // Fetch page data when pagination or IDs change
  useEffect(() => {
    void fetchPageData();
  }, [fetchPageData]);

  // Pagination handlers
  const handleSetPage = useCallback((newPage: number) => {
    setPage((currentPage) => {
      if (currentPage === newPage) {
        return currentPage; // Prevent unnecessary updates
      }
      return newPage;
    });
  }, []);

  const handleSetRowsPerPage = useCallback((newRowsPerPage: number) => {
    setRowsPerPage((currentRowsPerPage) => {
      if (currentRowsPerPage === newRowsPerPage) {
        return currentRowsPerPage; // Prevent unnecessary updates
      }
      setPage(0); // Reset to first page
      return newRowsPerPage;
    });
  }, []);

  // Reload function
  const reload = useCallback(async () => {
    await fetchIds();
    setPage(0);
  }, [fetchIds]);

  // Stays on the current page, or moves back to the last one when the reloaded list no longer reaches it
  const reloadKeepingPage = useCallback(async () => {
    const ids = await fetchIds();

    if (ids) {
      const lastPage = Math.max(Math.ceil(ids.length / rowsPerPage) - 1, 0);
      setPage((currentPage) => Math.min(currentPage, lastPage));
    }
  }, [fetchIds, rowsPerPage]);

  const refreshPageData = useCallback(async (): Promise<PageResultType[]> => {
    return await fetchPageData();
  }, [fetchPageData]);

  const updatePageResult = useCallback((item: PageResultType) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setPageResults((prevResults) => prevResults.map((prevItem) => (prevItem.id === item.id ? item : prevItem)));
  }, []);

  return {
    pagination: {
      page,
      rowsPerPage,
      totalCount,
      resultsTruncated,
      setPage: handleSetPage,
      setRowsPerPage: handleSetRowsPerPage,
    },
    pageResults,
    extraData,
    pageMeta,
    isLoading,
    isError,
    reload,
    reloadKeepingPage,
    refreshPageData,
    updatePageResult,
  };
};

export default useIdBasedPagination;
